import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { BaseFilter } from 'src/app/common/filters';
import { FilterHelper } from 'src/app/common/helper';
import { PagedData, ProductBatch, ReturnData } from 'src/app/model';
import { ERPWarehouse, ERPWhStocks } from 'src/app/models/erpwarehouse';
import { ERPSpdocline } from 'src/app/models/spdocline';
import { BaseService } from '../base.service';
import { Observable } from 'rxjs';
import { Router } from '@angular/router';
import { NotificationService } from '../notification.service';
import { ErpWarehouseProductStock } from '../../models/erp-warehouse-product-stock';
import { ProductWarehouseStockInfoComponent } from '../../components/common/product-warehouse-stock-info/product-warehouse-stock-info.component';
import { MatDialog } from '@angular/material/dialog';
import { take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ErpWarehouseService extends BaseService {
  router = inject(Router);
  dialog = inject(MatDialog);
  notificationService = inject(NotificationService);
  productListWarehouses = signal<ERPWarehouse[]>([]);
  productStocksPerWarehouse = signal<ERPWhStocks[]>([]);
  warehouseProductStocks = signal<ErpWarehouseProductStock[]>([]);

  constructor(private httpClient: HttpClient) {
    super(httpClient);
  }

  readonly defaultProductTableColumns = [
    'id',
    'product_name',
    'qr-scanner-product',
    'qty',
    'unit',
    'item_price',
    'discount',
    'discount2',
    'vat',
    'warehouse',
    'qr-scanner-warehouse',
    'product-warehouse-stock',
    'remarks',
    'del',
  ];

  private readonly ITDProductTableColumns = [
    'qr-scanner-product',
    'qr-scanner-warehouse',
    'qty',
    'product_name',
    'warehouse',
    'product-warehouse-stock',
    'del',
  ];

  public getActiveWarehouses(): Observable<ERPWarehouse[]> {
    return this.get<ERPWarehouse[]>(this.apiUrl + 'erp/warehouses');
  }

  public getWarehousesTree(): Observable<ERPWarehouse> {
    return this.get<ERPWarehouse>(this.apiUrl + 'erp/warehousestree');
  }

  public storeWarehouse(
    warehoust: ERPWarehouse
  ): Observable<ReturnData<ERPWarehouse>> {
    return this.post<ReturnData<ERPWarehouse>>(
      this.apiUrl + 'erp/warehouses',
      warehoust
    );
  }

  public updateWarehouse(
    warehoust: ERPWarehouse
  ): Observable<ReturnData<ERPWarehouse>> {
    return this.put<ReturnData<ERPWarehouse>>(
      this.apiUrl + 'erp/warehouses/' + warehoust.id,
      warehoust
    );
  }

  public getAllWarehouses(filters: BaseFilter): Observable<ERPWarehouse[]> {
    let queryString = FilterHelper.QueryStringHelper(filters);
    return this.get<ERPWarehouse[]>(
      this.apiUrl + 'erp/warehouses_all' + queryString
    );
  }

  public getAllWarehousesPaged(
    filters: BaseFilter
  ): Observable<PagedData<ERPWarehouse[]>> {
    let queryString = FilterHelper.QueryStringHelper(filters, true);
    return this.get<PagedData<ERPWarehouse[]>>(
      this.apiUrl + 'erp/warehouses_paged' + queryString
    );
  }

  public getSingleWarehouse(id: number): Observable<ERPWarehouse> {
    return this.get<ERPWarehouse>(this.apiUrl + 'erp/warehouses/' + id);
  }

  public getProductWhStocks(product_id: number): Observable<ERPWhStocks[]> {
    return this.get<ERPWhStocks[]>(
      this.apiUrl + 'erp/product/' + product_id + '/stocks'
    );
  }

  public getWarehouseProductStocks(
    warehouseId: number
  ): Observable<ErpWarehouseProductStock[]> {
    return this.get<ErpWarehouseProductStock[]>(
      this.apiUrl + 'erp/warehouse/' + warehouseId + '/stocks'
    );
  }

  public getProductActiveBatches(
    product_id: number
  ): Observable<ProductBatch[]> {
    return this.get<ProductBatch[]>(
      this.apiUrl + 'erp/product/' + product_id + '/active-batches'
    );
  }

  public getProductWhHistory(
    filters: BaseFilter,
    product_id: number,
    warehouse_id: number
  ): Observable<PagedData<ERPSpdocline>> {
    return this.get<PagedData<ERPSpdocline>>(
      this.apiUrl +
        'erp/history/' +
        product_id +
        '/' +
        warehouse_id +
        '/warehouse?page=' +
        filters.currentPage +
        '&pageSize=' +
        filters.pageSize
    );
  }

  public getWarehouses() {
    return this.getActiveWarehouses()
      .pipe(take(1))
      .subscribe((res) => {
        this.productListWarehouses.set(this.filterWarehouses(res));
        return true;
      });
  }

  filterWarehouses(activeWarehouses: ERPWarehouse[]) {
    // Define the filtering urls
    const routesForLeafWarehouses = [
      'erp_document',
      'pickingorder',
      'receivinglist',
      'inventory',
    ];
    const routesForCentralWarehouses = ['quote', 'order'];

    // Fetch the active url
    const activeRoute = this.router.url.split('/')[1];

    // Return the warehouses that are not parents of another warehouse
    if (routesForLeafWarehouses.includes(activeRoute)) {
      return this.findLeafWarehouses(activeWarehouses);
    }

    // Return the main warehouse, its immediate children and their children
    if (routesForCentralWarehouses.includes(activeRoute)) {
      return this.findThreeTopTierWarehouses(activeWarehouses);
    }

    // In case of different url, return all the active warehouses
    return activeWarehouses;
  }

  private findLeafWarehouses(activeWarehouses: ERPWarehouse[]) {
    const warehouseParents = activeWarehouses.map(
      (warehouse) => warehouse.warehouse_id
    );
    const uniqueParents = [...new Set(warehouseParents)];
    return activeWarehouses.filter(
      (warehouse) => !uniqueParents.includes(warehouse.id)
    );
  }

  private findThreeTopTierWarehouses(activeWarehouses: ERPWarehouse[]) {
    const mainWarehouse = activeWarehouses.filter(
      (warehouse) => warehouse.warehouse_id === null
    )[0];
    if (mainWarehouse) {
      const mainWarehouseChildren = activeWarehouses.filter(
        (warehouse) => warehouse.warehouse_id === mainWarehouse.id
      );
      const mainWarehouseChildrenIds = mainWarehouseChildren.map(
        (warehouse) => warehouse.id
      );
      const thirdTierWarehouses = activeWarehouses.filter((warehouse) =>
        mainWarehouseChildrenIds.includes(warehouse.warehouse_id)
      );
      return [mainWarehouse, ...mainWarehouseChildren, ...thirdTierWarehouses];
    } else {
      return [];
    }
  }

  changeProductTableColumns(code: string) {
    if (code === 'ITD') {
      return this.ITDProductTableColumns;
    } else {
      return this.defaultProductTableColumns;
    }
  }

  setWarehouseForLine(
    line: ERPSpdocline,
    code: any,
    spdocLines: ERPSpdocline[],
    warehouses: ERPWarehouse[]
  ) {
    const warehouseFromScanner = warehouses.find((w) => w.code === code);
    if (warehouseFromScanner) {
      this.notificationService.showSnackbarMessage('Messages.warehouseFound');
      const updatedLine = { ...line };
      updatedLine.warehouse_id = warehouseFromScanner?.id;
      const updatedConfQuoteLines = [...spdocLines];
      const index = spdocLines.findIndex((q) => q.id === updatedLine.id);
      if (index !== -1) {
        updatedConfQuoteLines[index] = updatedLine;
      }
      return updatedConfQuoteLines;
    } else {
      this.notificationService.showSnackbarMessage(
        'Messages.warehouseNotFound'
      );
      return spdocLines;
    }
  }

  selectDestinationWarehouse(code: any, warehouses: ERPWarehouse[]) {
    const warehouse = warehouses.find((warehouse) => warehouse.code === code);
    if (warehouse) {
      this.notificationService.showSnackbarMessage('Messages.warehouseFound');
      return warehouse.id;
    }
    this.notificationService.showSnackbarMessage('Messages.warehouseNotFound');
    return null;
  }

  fetchProductStockInfo(productId: number) {
    this.getProductWhStocks(productId)
      .pipe(take(1))
      .subscribe((res) => {
        this.productStocksPerWarehouse.set(res);
      });
  }

  fetchWarehouseName(warehouse_id: number) {
    return (
      this.productListWarehouses().find((w) => w.id === warehouse_id).title ??
      ''
    );
  }

  fetchWarehouseProductStocks(warehouseId: number) {
    this.getWarehouseProductStocks(warehouseId)
      .pipe(take(1))
      .subscribe((res) => {
        this.warehouseProductStocks.set(res);
      });
  }

  openProductWarehouseStockPopUp(line: ERPSpdocline) {
    this.dialog.open(ProductWarehouseStockInfoComponent, {
      data: {
        line: line,
        productName: line.product_name,
      },
      height: '80%',
      width: '80%',
    });
  }
}
